import { lazy, Suspense, useEffect } from "react"
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom"
import { bootstrapAuth } from "@/lib/bootstrap-auth"
import { ErrorBoundary } from "@/components/error-boundary"
import { PostHogPageTracker } from "@/components/PostHogProvider"
// Layouts / guards stay eagerly imported: they're tiny, shared by every
// route, and lazy-loading them would only add a render waterfall.
import { SuperLayout } from "@/components/super-layout"
import { ProctorLayout } from "@/components/proctor-layout"
import { ClientLayout } from "@/components/client-layout"
import { EditorLayout } from "@/layouts/EditorLayout"
import { ProtectedRoute } from "@/components/protected-route"
import { ModuleGuard } from "@/components/ModuleGuard"

/* If someone hits /test/<48-hex-token>, the URL was meant for the
 * invitation flow (/test/invite/:token). Detect a 48-char hex string and
 * redirect — old links sent before /test/invite/:token existed still work. */
function TestRouteOrInvite({ children }: { children: React.ReactNode }) {
  const { assessmentId } = useParams<{ assessmentId: string }>()
  if (assessmentId && /^[0-9a-f]{48}$/i.test(assessmentId)) {
    return <Navigate to={`/test/invite/${assessmentId}`} replace />
  }
  return <>{children}</>
}

/* Suspense fallback while a route's JS chunk is fetched. */
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    </div>
  )
}

// Every page is a default export, so React.lazy() works directly. Each
// becomes its own code-split chunk fetched on first navigation.
const NotFoundPage = lazy(() => import("@/pages/errors/NotFound"))
const CandidateFeedbackPage = lazy(() => import("@/pages/candidate/Feedback"))
const TestInvitePage = lazy(() => import("@/pages/candidate/TestInvitePage"))
const ClientLoginPage = lazy(() => import("@/pages/client/Login"))
const ClientSignupPage = lazy(() => import("@/pages/client/Signup"))
const ClientForgotPasswordPage = lazy(() => import("@/pages/client/ForgotPassword"))
const ClientDashboardPage = lazy(() => import("@/pages/client/Dashboard"))
const ProjectsPage = lazy(() => import("@/pages/client/Projects"))
const AssessmentListPage = lazy(() => import("@/pages/client/AssessmentList"))
const AssessmentSetupPage = lazy(() => import("@/pages/client/AssessmentSetup"))
const AssessmentPreviewPage = lazy(() => import("@/pages/client/AssessmentPreview"))
const CandidatesPage = lazy(() => import("@/pages/client/Candidates"))
const PsychometricsPage = lazy(() => import("@/pages/client/psychometrics/Psychometrics"))
const PsychometricCandidatePage = lazy(
  () => import("@/pages/client/psychometrics/CandidateProfile"),
)
const ClientMastersPage = lazy(() => import("@/pages/client/Masters"))
const CohortsPage = lazy(() => import("@/pages/client/Cohorts"))
const CohortDetailPage = lazy(() => import("@/pages/client/CohortDetail"))
const CohortReportPage = lazy(() => import("@/pages/client/reports/CohortReport"))
const ManageProctorsPage = lazy(() => import("@/pages/client/ManageProctors"))
const ProfilePage = lazy(() => import("@/pages/client/Profile"))
const ClientSupportPage = lazy(() => import("@/pages/client/Support"))
const ActionCenterPage = lazy(() => import("@/pages/client/ActionCenter"))
const MultiraterProjectsPage = lazy(() => import("@/pages/client/multirater/MultiraterProjects"))
const MultiraterListPage = lazy(() => import("@/pages/client/multirater/MultiraterList"))
const MultiraterSetupPage = lazy(() => import("@/pages/client/multirater/MultiraterSetup"))
const MultiraterReportsPage = lazy(() => import("@/pages/client/multirater/MultiraterReports"))
const MultiraterReportViewPage = lazy(() => import("@/pages/client/multirater/MultiraterReportView"))
const MultiraterTakePage = lazy(() => import("@/pages/multirater/MultiraterTakePage"))
const MultiraterSubjectReportPage = lazy(() => import("@/pages/MultiraterSubjectReport"))
const ReportsListPage = lazy(() => import("@/pages/client/reports/ReportsList"))
const CandidateReportPage = lazy(() => import("@/pages/client/reports/CandidateReport"))
const CertificateViewPage = lazy(() => import("@/pages/client/reports/CertificateView"))
const CertificatePublicPage = lazy(() => import("@/pages/CertificatePublic"))
const SuperLoginPage = lazy(() => import("@/pages/super/Login"))
const SuperDashboardPage = lazy(() => import("@/pages/super/Dashboard"))
const ManageClientsPage = lazy(() => import("@/pages/super/ManageClients"))
const ClientProfilePage = lazy(() => import("@/pages/super/ClientProfile"))
const SubscriptionManagerPage = lazy(() => import("@/pages/super/SubscriptionManager"))
const CouponsDiscountsPage = lazy(() => import("@/pages/super/CouponsDiscounts"))
const BillingPaymentPage = lazy(() => import("@/pages/super/BillingPayment"))
const SupportPage = lazy(() => import("@/pages/super/Support"))
const SuperProfilePage = lazy(() => import("@/pages/super/SuperProfile"))
const SuperActionCenterPage = lazy(() => import("@/pages/super/ActionCenter"))
const MastersPage = lazy(() => import("@/pages/super/Masters"))
const ProctorLoginPage = lazy(() => import("@/pages/proctor/Login"))
const ProctorDashboardPage = lazy(() => import("@/pages/proctor/Dashboard"))
const LiveFeedPage = lazy(() => import("@/pages/proctor/LiveFeed"))
const AuthorizeCandidatePage = lazy(() => import("@/pages/proctor/AuthorizeCandidate"))
const ProctorReportsPage = lazy(() => import("@/pages/proctor/Reports"))
const ProctorFlagsPage = lazy(() => import("@/pages/proctor/Flags"))
const CandidateVerifyOtpPage = lazy(() => import("@/pages/candidate/VerifyOtp"))
const TestLandingPage = lazy(() => import("@/pages/candidate/TestLanding"))
const SystemCheckPage = lazy(() => import("@/pages/candidate/SystemCheck"))
const CandidateRegistrationPage = lazy(() => import("@/pages/candidate/CandidateRegistration"))
const TestInstructionsPage = lazy(() => import("@/pages/candidate/TestInstructions"))
const WaitingAuthorizationPage = lazy(() => import("@/pages/candidate/WaitingAuthorization"))
const TestWindowPage = lazy(() => import("@/pages/candidate/TestWindow"))
const TestResultPage = lazy(() => import("@/pages/candidate/TestResult"))
const PsychometricProfilePage = lazy(() => import("@/pages/candidate/PsychometricProfile"))
const PsychometricPrintPage = lazy(() => import("@/pages/print/PsychometricPrint"))
/* DEV ONLY. import.meta.env.DEV is statically false in a production build, so
 * Rollup drops both the route and the lazy import — the preview page is not
 * merely unreachable in production, it is not in the bundle. */
const CategoryCardPreview = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/CategoryCardPreview"))
  : null
const DashboardPlaceholder = lazy(() => import("@/pages/Dashboard"))
// QR Assessment module — lazy-loaded, removable as a unit.
const QrDashboardPage = lazy(() => import("@/pages/client/qr/QrDashboard"))
const QrQuizBuilderPage = lazy(() => import("@/pages/client/qr/QrQuizBuilder"))
const QrLandingPage = lazy(() => import("@/pages/qr/QrLanding"))

function App() {
  // S6 5b/A5: reconstruct identity from the cookie session on load. A hard
  // refresh under httpOnly cookies can't read the JWT in JS, so we ask the
  // server who we are via GET /auth/me (guarded by the auth_status hint).
  useEffect(() => {
    void bootstrapAuth()
  }, [])

  // BrowserRouter wraps ErrorBoundary. If a route component throws,
  // ErrorBoundary renders <ServerErrorPage>, whose buttons now do full page
  // loads (a plain <a href="/">, not <Link>), so it no longer needs a Router
  // ancestor. The order is kept as it was.
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <PostHogPageTracker />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Client Admin auth */}
        <Route path="/login" element={<ClientLoginPage />} />
        <Route path="/signup" element={<ClientSignupPage />} />
        <Route path="/forgot-password" element={<ClientForgotPasswordPage />} />
        <Route path="/dashboard" element={<Navigate to="/client/dashboard" replace />} />

        {/* Client Admin app */}
        <Route
          path="/client"
          element={
            <ProtectedRoute allowedRoles={["CLIENT_ADMIN"]}>
              <ClientLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<ClientDashboardPage />} />
          <Route path="projects" element={<ModuleGuard module="assessments"><ProjectsPage /></ModuleGuard>} />
          <Route path="projects/:id/assessments" element={<ModuleGuard module="assessments"><AssessmentListPage /></ModuleGuard>} />
          <Route path="assessments/:id/preview" element={<ModuleGuard module="assessments"><AssessmentPreviewPage /></ModuleGuard>} />
          <Route path="multirater" element={<ModuleGuard module="multirater"><MultiraterProjectsPage /></ModuleGuard>} />
          <Route path="multirater/projects/:id" element={<ModuleGuard module="multirater"><MultiraterListPage /></ModuleGuard>} />
          <Route path="multirater/:id/setup" element={<ModuleGuard module="multirater"><MultiraterSetupPage /></ModuleGuard>} />
          <Route path="multirater/:id/reports" element={<ModuleGuard module="multirater"><MultiraterReportsPage /></ModuleGuard>} />
          <Route path="multirater/:id/reports/:subjectId" element={<ModuleGuard module="multirater"><MultiraterReportViewPage /></ModuleGuard>} />
          {/* QR Assessment module — admin dashboard inside the standard
              ClientLayout. The /qr/:sessionId public participant page is
              registered separately at the bottom (no layout). */}
          <Route path="qr" element={<ModuleGuard module="qrQuiz"><QrDashboardPage /></ModuleGuard>} />
          <Route path="qr/quiz/new" element={<ModuleGuard module="qrQuiz"><QrQuizBuilderPage /></ModuleGuard>} />
          <Route path="qr/quiz/:quizId" element={<ModuleGuard module="qrQuiz"><QrQuizBuilderPage /></ModuleGuard>} />
          <Route path="cohorts" element={<ModuleGuard module="assessments"><CohortsPage /></ModuleGuard>} />
          <Route path="cohorts/:id" element={<ModuleGuard module="assessments"><CohortDetailPage /></ModuleGuard>} />
          <Route path="cohorts/:cohortId/assessments/:assessmentId/report" element={<ModuleGuard module="assessments"><CohortReportPage /></ModuleGuard>} />
          <Route path="candidates" element={<CandidatesPage />} />
          <Route
            path="psychometrics"
            element={
              <ModuleGuard module="psychometrics">
                <PsychometricsPage />
              </ModuleGuard>
            }
          />
          <Route
            path="psychometrics/candidates/:candidateId"
            element={
              <ModuleGuard module="psychometrics">
                <PsychometricCandidatePage />
              </ModuleGuard>
            }
          />
          <Route path="proctors" element={<ModuleGuard module="proctoring"><ManageProctorsPage /></ModuleGuard>} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="masters" element={<ClientMastersPage />} />
          <Route path="support" element={<ClientSupportPage />} />
          <Route path="support/:ticketId" element={<ClientSupportPage />} />
          <Route path="action-center" element={<ActionCenterPage />} />
          <Route path="reports" element={<ReportsListPage />} />
          <Route path="reports/attempts/:attemptId" element={<CandidateReportPage />} />
          <Route path="assessments/:id/certificate" element={<CertificateViewPage />} />
        </Route>

        {/* Assessment Builder — full-screen editor, NO app sidebar.
            Same auth + CLIENT_ADMIN guard as the rest of /client, but a
            dedicated layout so the builder's own step nav isn't doubled
            up with the main app sidebar. */}
        <Route
          path="/client/assessments/:id/setup"
          element={
            <ProtectedRoute allowedRoles={["CLIENT_ADMIN"]}>
              {/* assistant: this route bypasses ClientLayout, where the launcher
                  is mounted for the rest of /client, so it has to opt in here or
                  the builder is the one admin screen without it. */}
              <EditorLayout assistant />
            </ProtectedRoute>
          }
        >
          <Route index element={<AssessmentSetupPage />} />
        </Route>

        {/* Proctor Live Feed — full-screen monitoring command center, NO app
            sidebar (like the Assessment Builder). Same auth guards as before;
            one route per viewer role so the right credentials are required. */}
        <Route
          path="/proctor/live/:assessmentId"
          element={
            <ProtectedRoute allowedRoles={["PROCTOR"]} redirectTo="/proctor/login">
              {/* NO assistant prop, deliberately. The assistant is CLIENT_ADMIN
                  only; omitting it here means a proctor's page never even has a
                  reason to fetch the chunk. */}
              <EditorLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<LiveFeedPage />} />
        </Route>
        <Route
          path="/client/assessments/:assessmentId/live-feed"
          element={
            <ProtectedRoute allowedRoles={["CLIENT_ADMIN"]}>
              {/* Same gap as the builder, and it was never reported: this is a
                  CLIENT_ADMIN route on EditorLayout, so it had no launcher
                  either. */}
              <EditorLayout assistant />
            </ProtectedRoute>
          }
        >
          <Route index element={<LiveFeedPage />} />
        </Route>

        {/* Super Admin */}
        <Route path="/super/login" element={<SuperLoginPage />} />
        <Route
          path="/super"
          element={
            <ProtectedRoute allowedRoles={["SUPER_ADMIN"]} redirectTo="/super/login">
              <SuperLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<SuperDashboardPage />} />
          <Route path="clients" element={<ManageClientsPage />} />
          <Route path="clients/:id" element={<ClientProfilePage />} />
          <Route path="subscriptions" element={<SubscriptionManagerPage />} />
          <Route path="coupons" element={<CouponsDiscountsPage />} />
          <Route path="billing" element={<BillingPaymentPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="masters" element={<MastersPage />} />
          <Route path="profile" element={<SuperProfilePage />} />
          <Route path="action-center" element={<SuperActionCenterPage />} />
        </Route>

        {/* Proctor */}
        <Route path="/proctor/login" element={<ProctorLoginPage />} />
        <Route
          path="/proctor"
          element={
            <ProtectedRoute allowedRoles={["PROCTOR"]} redirectTo="/proctor/login">
              <ProctorLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<ProctorDashboardPage />} />
          <Route path="authorize" element={<AuthorizeCandidatePage />} />
          <Route path="authorize/:assessmentId" element={<AuthorizeCandidatePage />} />
          <Route path="flags" element={<ProctorFlagsPage />} />
          <Route path="reports" element={<ProctorReportsPage />} />
        </Route>

        {/* Candidate */}
        <Route path="/candidate/verify" element={<CandidateVerifyOtpPage />} />
        <Route
          path="/candidate/dashboard"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <DashboardPlaceholder />
            </ProtectedRoute>
          }
        />

        {/* Candidate test flow (public landing + protected steps) */}
        {/* Public invitation landing — looks up token, redirects to test or mtest */}
        <Route path="/test/invite/:token" element={<TestInvitePage />} />
        <Route
          path="/test/:assessmentId"
          element={
            <TestRouteOrInvite>
              <TestLandingPage />
            </TestRouteOrInvite>
          }
        />
        <Route
          path="/test/:assessmentId/system-check"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <SystemCheckPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/:assessmentId/register"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <CandidateRegistrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/:assessmentId/instructions"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <TestInstructionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/:assessmentId/waiting"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <WaitingAuthorizationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/:assessmentId/attempt/:attemptId"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <TestWindowPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feedback/:attemptId"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <CandidateFeedbackPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/result/:attemptId"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <TestResultPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/profile/:attemptId"
          element={
            <ProtectedRoute allowedRoles={["CANDIDATE"]} redirectTo="/candidate/verify">
              <PsychometricProfilePage />
            </ProtectedRoute>
          }
        />


        {/* Multirater participant flow (public via token) */}
        <Route path="/mtest/:token" element={<MultiraterTakePage />} />
        <Route path="/multirater/report/:token" element={<MultiraterSubjectReportPage />} />

        {/* Psychometric profile, print layout — PUBLIC, and the single-use PDF
            token in the path is the whole authorization. The only caller is the
            backend's own headless Chromium, which arrives with no session and
            cannot be wrapped in ProtectedRoute. Deliberately carries no screen
            chrome: this route exists to be captured, not read. */}
        <Route path="/print/psychometric/:token" element={<PsychometricPrintPage />} />
        {CategoryCardPreview ? (
          <Route path="/dev/category-card" element={<CategoryCardPreview />} />
        ) : null}

        {/* QR Assessment — public participant page (no layout, no auth) */}
        <Route path="/qr/:sessionId" element={<QrLandingPage />} />

        {/* Public certificate verification */}
        <Route path="/certificate/:token" element={<CertificatePublicPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  )
}

export default App
